import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useConfirm } from "@/contexts/ConfirmContext";
import { useTranslation } from "react-i18next";
import { springs } from "@/lib/motion";

// Clickable "Language" text (no globe). Selecting a different language asks for
// confirmation before it is applied.
export const LanguageSwitcher = () => {
  const { lang, setLang, languages } = useLanguage();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = async (l) => {
    setOpen(false);
    if (l.code === lang) return;
    const ok = await confirm({
      title: t("language.confirm_title"),
      message: t("language.confirm_message", { lang: l.label }),
      confirmText: t("language.confirm_yes"),
      cancelText: t("language.confirm_no"),
    });
    if (ok) setLang(l.code);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="language-switcher"
        className="flex items-center gap-1.5 px-2.5 py-2 rounded-full text-sm font-medium transition-colors hover:bg-[color:var(--bg-subtle)]"
        style={{ color: "var(--text-primary)" }}
      >
        {t("common.language")}
        <ChevronDown className="w-4 h-4" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={springs.widget}
            className="glass-card absolute left-0 sm:left-auto sm:right-0 mt-2 py-2 w-44 max-w-[calc(100vw-2rem)] z-50 max-h-[70vh] overflow-y-auto"
            style={{ background: "var(--bg-surface)", borderRadius: 20 }}
            data-testid="language-menu"
          >
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => pick(l)}
                data-testid={`lang-${l.code}`}
                className="w-full flex items-center justify-between px-4 py-2 text-sm transition-colors hover:bg-[color:var(--bg-subtle)]"
                style={{ color: "var(--text-primary)" }}
              >
                <span>{l.label}</span>
                {lang === l.code && <Check className="w-4 h-4" style={{ color: "var(--accent-primary)" }} />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
